'use client'

import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Zap, Play, ArrowRight } from 'lucide-react'
import Link from 'next/link'

export interface ClipPreview {
  title: string
  start_time: number
  end_time: number
  duration: number
  virality_score: number
  virality_reason: string
  transcript_segment: string
}

interface ClipPreviewPanelProps {
  clips: ClipPreview[]
  videoId: string
  isLoading?: boolean
  onTryFree?: () => void
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  if (mins > 0) {
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }
  return `${secs}s`
}

function normalizeScore(score: number): number {
  return score <= 1 ? Math.round(score * 100) : Math.round(score)
}

function getScoreTextColor(score: number): string {
  const n = normalizeScore(score)
  if (n >= 90) return "text-emerald-500"
  if (n >= 75) return "text-lime-500"
  if (n >= 60) return "text-yellow-500"
  return "text-muted-foreground"
}

function PreviewClipCard({ clip, videoId, index }: { clip: ClipPreview; videoId: string; index: number }) {
  const [playing, setPlaying] = useState(false)
  // YouTube HQ thumbnails at different video timestamps: hq1, hq2, hq3 (480x360)
  const thumbIndex = (index % 3) + 1
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hq${thumbIndex}.jpg`
  const startSeconds = Math.floor(clip.start_time)
  const endSeconds = Math.floor(clip.end_time)
  const score = normalizeScore(clip.virality_score)

  const captionText = clip.transcript_segment.length > 60
    ? clip.transcript_segment.slice(0, 57) + '...'
    : clip.transcript_segment

  return (
    <div className="group">
      <Card className="overflow-hidden bg-zinc-900/50 border-zinc-800 hover:border-[#CC3E21]/40 transition-colors h-full flex flex-col">
        {/* Thumbnail or inline player */}
        <div className="relative aspect-[9/16] bg-black">
          {playing ? (
            <button onClick={() => setPlaying(false)} className="absolute inset-0 w-full h-full cursor-pointer z-10">
              <div className="absolute inset-0 overflow-hidden">
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}?start=${startSeconds}&end=${endSeconds}&autoplay=1&rel=0&modestbranding=1&controls=0&showinfo=0&iv_load_policy=3&disablekb=1&playsinline=1`}
                  className="absolute top-1/2 left-1/2 w-[300%] h-[300%] border-0 pointer-events-none"
                  style={{ transform: 'translate(-50%, -50%)' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  title={clip.title}
                />
              </div>
            </button>
          ) : (
            <button onClick={() => setPlaying(true)} className="absolute inset-0 w-full h-full cursor-pointer">
              <img
                src={thumbnailUrl}
                alt={clip.title}
                className="w-full h-full object-cover"
              />

              {/* Gradient overlay for readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

              {/* Play button */}
              <div className="absolute inset-0 flex items-center justify-center group-hover:bg-black/10 transition-colors">
                <div className="w-10 h-10 bg-[#CC3E21]/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Play className="h-4 w-4 text-white ml-0.5" fill="white" />
                </div>
              </div>

              {/* Score badge — top left */}
              <div className="absolute top-2 left-2">
                <span className={`text-xs font-bold ${getScoreTextColor(clip.virality_score)} bg-black/60 px-1.5 py-0.5 rounded`}>
                  {score}%
                </span>
              </div>

              {/* Duration badge — top right */}
              <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                {formatDuration(clip.duration)}
              </div>

              {/* Caption overlay — bottom, styled like real video captions */}
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <p className="text-[11px] font-semibold text-white leading-tight text-center drop-shadow-lg"
                   style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.5)' }}>
                  &ldquo;{captionText}&rdquo;
                </p>
              </div>
            </button>
          )}
        </div>

        {/* Virality reason below thumbnail */}
        <div className="p-2 bg-zinc-900">
          <p className="text-[10px] text-zinc-400 line-clamp-2 leading-relaxed">
            {clip.virality_reason}
          </p>
        </div>
      </Card>
    </div>
  )
}

export default function ClipPreviewPanel({ clips, videoId, isLoading, onTryFree }: ClipPreviewPanelProps) {
  if (isLoading) {
    return (
      <Card className="p-6 bg-zinc-900/50 border-zinc-800 mt-4">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-[#CC3E21] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-zinc-400">Scanning for viral clip moments...</span>
        </div>
      </Card>
    )
  }

  if (!clips || clips.length === 0) return null

  const clipToolUrl = `/tools/youtube-viral-clips?url=${encodeURIComponent(`https://youtube.com/watch?v=${videoId}`)}`

  return (
    <div className="mt-4 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Zap className="h-4 w-4 text-[#ff6b4a]" />
        <span className="text-sm font-semibold text-white">
          Viral Clip Moments Detected
        </span>
        <span className="text-xs bg-[#CC3E21]/20 text-[#ff6b4a] px-2 py-0.5 rounded-full font-medium">
          {clips.length}
        </span>
      </div>

      {/* CTA Banner — above clips */}
      {onTryFree ? (
        <button onClick={onTryFree} className="w-full text-left">
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#CC3E21] to-[#ff6b4a] p-4 text-white group hover:shadow-lg hover:shadow-[#CC3E21]/20 transition-all cursor-pointer">
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-left">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Zap className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-bold">
                    Get Full AI Clip Detection
                  </h4>
                  <p className="text-xs text-white/80">
                    AI scores each moment, adds captions &amp; B-roll. Export ready-to-post clips.
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto px-4 py-2 bg-white text-[#CC3E21] text-sm font-bold rounded-lg group-hover:scale-105 transition-transform flex-shrink-0">
                Try Free <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </div>
        </button>
      ) : (
        <Link href={clipToolUrl}>
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#CC3E21] to-[#ff6b4a] p-4 text-white group hover:shadow-lg hover:shadow-[#CC3E21]/20 transition-all cursor-pointer">
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-left">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Zap className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-bold">
                    Get Full AI Clip Detection
                  </h4>
                  <p className="text-xs text-white/80">
                    AI scores each moment, adds captions &amp; B-roll. Export ready-to-post clips.
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto px-4 py-2 bg-white text-[#CC3E21] text-sm font-bold rounded-lg group-hover:scale-105 transition-transform flex-shrink-0">
                Try Free <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </div>
        </Link>
      )}

      {/* Clip cards grid — top 3 only (YouTube only provides 3 unique thumbnails per video) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {clips.slice(0, 3).map((clip, i) => (
          <PreviewClipCard key={i} clip={clip} videoId={videoId} index={i} />
        ))}
      </div>
    </div>
  )
}
